// COVD Receipts list — legacy parity with the original `covd_receipt.php`.
//
// Tabs: Unsent / Sent (driven by the `is_sent` field on ReceiptSummary).
// Filters: search (customer name + reg), date range (from / to), driver,
//   status. The "search" param is shared with the legacy unique-id +
//   registration lookup; the server matches against customer_name and
//   car_registration. Driver filter mirrors the legacy diary integration.
// Per-row actions:
//   - View      → /receipts/:enc_id
//   - Edit      → /receipts/:enc_id/edit
//   - Send/Resend → POST /receipts/:enc_id/send (legacy `sendReceipt`)
//   - Download PDF → GET /receipts/:enc_id/pdf
//   - Approve / Reject (only when status = pending_review)
//   - Delete (admin only, soft delete with confirm modal)
// Bulk actions: select-all checkbox, "Send selected" (any staff, same
//   permission as the single send) and "Send all unsent" (manager/admin,
//   confirmation shows the count). Both show a per-receipt result summary.

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Coins,
  Download,
  Eye,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { StatsStrip } from '../../components/ui/StatsStrip/StatsStrip';
import { FilterBar } from '../../components/ui/FilterBar/FilterBar';
import { Table, type Column, type RowAction } from '../../components/ui/Table/Table';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { NotifiedBadge } from '../../components/ui/NotifiedBadge/NotifiedBadge';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import { useReceiptList } from './hooks/useReceiptList';
import { useUnsentCount } from './hooks/useUnsentCount';
import {
  useBulkSendReceipts,
  useDeleteReceipt,
  useSendAllUnsent,
} from './hooks/useReceiptMutations';
import { BulkSendSummary } from './components/BulkSendSummary';
import { SendReceiptModal } from './components/SendReceiptModal';
import { NameMismatchModal } from './components/NameMismatchModal';
import { NotesBadge } from '../../components/notes/NotesBadge';
import { NotesPanel } from '../../components/notes/NotesPanel';
import { useActiveDrivers } from '../bookings/hooks/useDriversList';
import { useToast } from '../../hooks/useToast';
import { useAuthStore } from '../../store/authStore';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import type { BulkSendResult, ReceiptStatus, ReceiptSummary } from '../../types/api';
import styles from './ReceiptsListPage.module.css';

const STATUS_MAP: Record<ReceiptStatus, BadgeVariant> = {
  pending_review: 'warning',
  approved: 'success',
  rejected: 'danger',
  archived: 'muted',
};

const STATUS_LABELS: Record<ReceiptStatus, string> = {
  pending_review: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
  archived: 'Archived',
};

type Tab = 'unsent' | 'sent';

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

async function downloadReceiptPdf(encId: string, uniqueId: string, onError: () => void) {
  try {
    const res = await apiClient.raw.get<Blob>(endpoints.receipts.pdf(encId), {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${uniqueId}.pdf`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    onError();
  }
}

export function ReceiptsListPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';
  const canSendAll = role === 'admin' || role === 'manager';

  const tab: Tab = params.get('tab') === 'sent' ? 'sent' : 'unsent';
  const status = (params.get('status') ?? '') as '' | ReceiptStatus;
  const dateFrom = params.get('date_from') ?? '';
  const dateTo = params.get('date_to') ?? '';
  const driverUuid = params.get('driver_uuid') ?? '';
  const search = params.get('q') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  // Deep-link from the Booking Search tab: the receipt's enc_id is passed
  // here as `booking_enc_id`. Filter the rendered list client-side so the
  // user lands on the single matching row.
  const bookingEncIdFilter = params.get('booking_enc_id') ?? '';

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const list = useReceiptList({
    page,
    is_sent: tab === 'sent',
    status: status || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    driver_uuid: driverUuid || undefined,
    search: search || undefined,
  });
  const drivers = useActiveDrivers();

  const deleteMutation = useDeleteReceipt();
  const bulkSend = useBulkSendReceipts();
  const sendAll = useSendAllUnsent();
  const [sendAllOpen, setSendAllOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkSendResult | null>(null);
  const unsentCount = useUnsentCount(sendAllOpen);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<ReceiptSummary | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ReceiptSummary | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [sendTarget, setSendTarget] = useState<ReceiptSummary | null>(null);
  const [mismatchTarget, setMismatchTarget] = useState<ReceiptSummary | null>(null);
  const [notesTarget, setNotesTarget] = useState<ReceiptSummary | null>(null);

  const allReceipts = list.data?.receipts ?? [];
  const receipts = useMemo(
    () =>
      bookingEncIdFilter.length > 0
        ? allReceipts.filter((r) => r.enc_id === bookingEncIdFilter)
        : allReceipts,
    [allReceipts, bookingEncIdFilter],
  );
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = bookingEncIdFilter.length > 0 ? 1 : (pagination?.totalPages ?? 1);

  function toggleAll() {
    if (selected.size === receipts.length && receipts.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(receipts.map((r) => r.enc_id)));
    }
  }

  function toggleOne(encId: string) {
    const next = new Set(selected);
    if (next.has(encId)) next.delete(encId);
    else next.add(encId);
    setSelected(next);
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function handleSendSelected() {
    if (selected.size === 0) return;
    try {
      const result = await bulkSend.mutateAsync({ receipt_ids: [...selected] });
      setBulkResult(result);
      clearSelection();
      void list.refetch();
    } catch {
      toast.error('Bulk send failed. Please try again.');
    }
  }

  async function handleSendAll() {
    try {
      const result = await sendAll.mutateAsync({});
      setBulkResult(result);
      setSendAllOpen(false);
      clearSelection();
      void list.refetch();
    } catch {
      setSendAllOpen(false);
      toast.error('Send all failed. Please try again.');
    }
  }

  async function handleApprove(receipt: ReceiptSummary) {
    try {
      await apiClient.post(endpoints.receipts.approve(receipt.enc_id));
      toast.success('Receipt approved');
      void list.refetch();
    } catch {
      toast.error('Failed to approve receipt.');
    }
  }

  async function handleReject(receipt: ReceiptSummary, reason: string) {
    if (!reason.trim()) {
      toast.error('A rejection reason is required.');
      return;
    }
    setRejecting(true);
    try {
      await apiClient.post(endpoints.receipts.reject(receipt.enc_id), { reason });
      toast.success('Receipt rejected');
      setRejectTarget(null);
      setRejectReason('');
      void list.refetch();
    } catch {
      toast.error('Failed to reject receipt.');
    } finally {
      setRejecting(false);
    }
  }

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, value: 0 };
    for (const r of receipts) {
      if (r.status === 'pending_review') c.pending += 1;
      if (r.status === 'approved') c.approved += 1;
      if (r.status === 'rejected') c.rejected += 1;
      c.value += r.amount;
    }
    return c;
  }, [receipts]);

  const columns: Column<ReceiptSummary>[] = useMemo(() => {
    return [
      {
        key: 'enc_id',
        header: '',
        width: '36px',
        render: (_v, row) => (
          <span className={styles.checkboxCell}>
            <input
              type="checkbox"
              aria-label={`Select receipt ${row.unique_id}`}
              checked={selected.has(row.enc_id)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleOne(row.enc_id)}
            />
          </span>
        ),
      },
      {
        key: 'unique_id',
        header: 'Receipt #',
        render: (_v, row) => (
          <button
            type="button"
            className={styles.refLink}
            onClick={() => navigate(`/collections/receipts/${encodeURIComponent(row.enc_id)}`)}
          >
            {row.unique_id}
          </button>
        ),
      },
      {
        key: 'receipt_date',
        header: 'Date',
        render: (_v, row) => formatDate(row.receipt_date),
      },
      {
        key: 'car_registration',
        header: 'Vehicle',
        render: (_v, row) => (
          <div className={styles.cellStack}>
            <span className={styles.regChip}>{row.car_registration}</span>
            <span className={styles.cellMuted}>{row.car_make_model}</span>
          </div>
        ),
      },
      {
        key: 'customer_name',
        header: 'Customer',
        render: (_v, row) => (
          <div className={styles.cellStack}>
            <span>{row.customer_name}</span>
            <span className={styles.cellMuted}>{row.customer_phone}</span>
          </div>
        ),
      },
      {
        key: 'notes_count',
        header: '',
        width: '40px',
        align: 'center',
        render: (_v, row) => (
          <NotesBadge
            count={row.notes_count}
            onClick={(e) => { e.stopPropagation(); setNotesTarget(row); }}
          />
        ),
      },
      {
        key: 'name_mismatch',
        header: '',
        width: '40px',
        align: 'center',
        render: (_v, row) =>
          row.name_mismatch ? (
            <button
              type="button"
              className={styles.mismatchBtn}
              onClick={() => setMismatchTarget(row)}
              title="Name mismatch detected — click to review"
              aria-label={`Name mismatch warning for receipt ${row.unique_id}`}
            >
              <AlertTriangle size={16} aria-hidden />
            </button>
          ) : null,
      },
      {
        key: 'driver_name',
        header: 'Driver',
        render: (_v, row) => row.driver_name ?? '—',
      },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        render: (_v, row) => formatMoney(row.amount),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) => (
          <StatusBadge
            status={row.status ?? null}
            statusMap={STATUS_MAP}
            formatLabel={(s) => STATUS_LABELS[s] ?? s}
          />
        ),
      },
      {
        key: 'sent_at',
        header: 'Sent',
        render: (_v, row) => {
          const isEmail = row.is_sent && row.sent_via === 'email';
          const isSms = row.is_sent && row.sent_via === 'sms';
          return (
            <NotifiedBadge
              emailSent={isEmail}
              smsSent={isSms}
              emailSentAt={isEmail ? row.sent_at : null}
              smsSentAt={isSms ? row.sent_at : null}
            />
          );
        },
      },
    ];
  }, [navigate, selected]);

  const allOnPageChecked =
    receipts.length > 0 && receipts.every((r) => selected.has(r.enc_id));

  const rowActions = (row: ReceiptSummary): RowAction<ReceiptSummary>[] => {
    const actions: RowAction<ReceiptSummary>[] = [
      {
        label: 'View',
        icon: Eye,
        onClick: () => navigate(`/collections/receipts/${encodeURIComponent(row.enc_id)}`),
        variant: 'ghost',
      },
      {
        label: 'Edit',
        icon: Pencil,
        onClick: () => navigate(`/collections/receipts/${encodeURIComponent(row.enc_id)}/edit`),
        variant: 'ghost',
      },
      {
        label: row.is_sent ? 'Resend' : 'Send',
        icon: row.is_sent ? RefreshCw : Send,
        onClick: () => setSendTarget(row),
        variant: 'ghost',
      },
      {
        label: 'Download PDF',
        icon: Download,
        onClick: () =>
          void downloadReceiptPdf(row.enc_id, row.unique_id, () =>
            toast.error('PDF generation failed. Please try again.'),
          ),
        variant: 'ghost',
      },
    ];
    if (row.status === 'pending_review') {
      actions.push({
        label: 'Approve',
        icon: CheckCircle2,
        onClick: () => void handleApprove(row),
        variant: 'ghost',
      });
      actions.push({
        label: 'Reject',
        icon: XCircle,
        onClick: () => {
          setRejectTarget(row);
          setRejectReason('');
        },
        variant: 'ghost',
      });
    }
    if (isAdmin) {
      actions.push({
        label: 'Delete',
        icon: Trash2,
        onClick: () => setDeleteTarget(row),
        variant: 'danger',
      });
    }
    return actions;
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="COVD Receipts"
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              variant="secondary"
              icon={ClipboardCheck}
              onClick={() => navigate('/collections/receipts/approval')}
            >
              Approval Queue
            </Button>
            {isAdmin ? (
              <Button
                variant="secondary"
                icon={Coins}
                onClick={() => navigate('/collections/payment-run')}
              >
                Payment Run
              </Button>
            ) : null}
            {isAdmin ? (
              <Button
                variant="secondary"
                icon={Download}
                onClick={() => navigate('/reports/collections/receipts-export')}
              >
                Bulk export
              </Button>
            ) : null}
            <Button variant="primary" icon={Plus} onClick={() => navigate('/collections/receipts/new')}>
              New Receipt
            </Button>
          </div>
        }
      />

      <StatsStrip
        ariaLabel="Receipt counters"
        tiles={[
          { key: 'all', label: 'All receipts', value: total },
          { key: 'pending', label: 'Pending', value: counts.pending },
          { key: 'approved', label: 'Approved', value: counts.approved },
          { key: 'page-value', label: 'Page value', value: formatMoney(counts.value) },
        ]}
      />

      <div role="tablist" className={styles.tabs} aria-label="Receipts tabs">
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'unsent'}
          tabIndex={tab === 'unsent' ? 0 : -1}
          className={tab === 'unsent' ? styles.tabActive : styles.tab}
          onClick={() => {
            clearSelection();
            setParam('tab', null);
          }}
        >
          Unsent
          {tab === 'unsent' && receipts.length > 0 ? (
            <span className={styles.tabBadge}>{receipts.length}</span>
          ) : null}
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'sent'}
          tabIndex={tab === 'sent' ? 0 : -1}
          className={tab === 'sent' ? styles.tabActive : styles.tab}
          onClick={() => {
            clearSelection();
            setParam('tab', 'sent');
          }}
        >
          Sent
          {tab === 'sent' && receipts.length > 0 ? (
            <span className={styles.tabBadge}>{receipts.length}</span>
          ) : null}
        </button>
      </div>

      {bookingEncIdFilter.length > 0 ? (
        <Card>
          <div className={styles.bookingFilterBanner} role="status">
            <span>Showing receipts for the selected booking-search result.</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setParam('booking_enc_id', null)}
            >
              Clear filter
            </Button>
          </div>
        </Card>
      ) : null}

      <Card flush>
        <FilterBar
          search={{
            value: search,
            onChange: (v) => setParam('q', v),
            placeholder: 'Search by customer, registration or receipt number…',
            ariaLabel: 'Search receipts',
          }}
        >
          <DatePicker
            aria-label="Date from"
            value={dateFrom}
            onChange={(e) => setParam('date_from', e.target.value)}
          />
          <DatePicker
            aria-label="Date to"
            value={dateTo}
            onChange={(e) => setParam('date_to', e.target.value)}
          />
          <Select
            aria-label="Driver filter"
            value={driverUuid}
            onChange={(e) => setParam('driver_uuid', e.target.value)}
            options={[
              { value: '', label: 'All drivers' },
              ...(drivers.data ?? []).map((d) => ({
                value: d.uuid,
                label: d.name,
              })),
            ]}
          />
          <Select
            aria-label="Status filter"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'pending_review', label: 'Pending review' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'archived', label: 'Archived' },
            ]}
          />
        </FilterBar>

        <div className={styles.bulkBar}>
          <label
            style={{ display: 'inline-flex', gap: '0.4rem', alignItems: 'center' }}
          >
            <input
              type="checkbox"
              aria-label="Select all on this page"
              checked={allOnPageChecked}
              onChange={toggleAll}
            />
            <span>
              {selected.size > 0
                ? `${selected.size} selected`
                : 'Select all on page'}
            </span>
          </label>
          <div className={styles.bulkBarActions}>
            {selected.size > 0 ? (
              <Button variant="ghost" size="sm" onClick={clearSelection}>
                Clear selection
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              icon={Send}
              disabled={selected.size === 0 || bulkSend.isPending}
              loading={bulkSend.isPending}
              onClick={() => void handleSendSelected()}
            >
              {selected.size > 0 ? `Send selected (${selected.size})` : 'Send selected'}
            </Button>
            {canSendAll ? (
              <Button
                variant="primary"
                size="sm"
                icon={Send}
                disabled={sendAll.isPending}
                onClick={() => setSendAllOpen(true)}
              >
                Send all unsent
              </Button>
            ) : null}
          </div>
        </div>

        {bulkResult !== null ? (
          <BulkSendSummary result={bulkResult} onDismiss={() => setBulkResult(null)} />
        ) : null}

        <Table
          columns={columns}
          data={receipts}
          keyField={(row) => row.enc_id}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <h3>No {tab === 'unsent' ? 'unsent' : 'sent'} receipts</h3>
              <p>
                {tab === 'unsent'
                  ? 'Create a new receipt or adjust your filters.'
                  : 'Sent receipts will appear here once delivered.'}
              </p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel="Receipts"
        />

        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? 50, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete receipt?"
        message={
          deleteTarget !== null
            ? `Delete receipt ${deleteTarget.unique_id} for ${deleteTarget.customer_name}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget === null) return;
          deleteMutation.mutate(deleteTarget.enc_id, {
            onSuccess: () => {
              setDeleteTarget(null);
              void list.refetch();
            },
          });
        }}
      />

      <ConfirmModal
        open={sendAllOpen}
        title="Send all unsent receipts?"
        message={
          unsentCount.isLoading
            ? 'Counting unsent receipts…'
            : unsentCount.data === 0
              ? 'There are no unsent receipts to send.'
              : `Send ${String(unsentCount.data ?? 0)} unsent receipt${unsentCount.data === 1 ? '' : 's'} to the customer${unsentCount.data === 1 ? '' : 's'}? Only approved receipts with an email address or phone number are included. At most 200 are sent per run.`
        }
        confirmLabel={
          unsentCount.data !== undefined && unsentCount.data > 0
            ? `Send ${String(Math.min(unsentCount.data, 200))}`
            : 'Send'
        }
        cancelLabel="Cancel"
        loading={sendAll.isPending}
        onCancel={() => setSendAllOpen(false)}
        onConfirm={() => {
          if (unsentCount.data === 0 || unsentCount.isLoading) return;
          void handleSendAll();
        }}
      />

      <SendReceiptModal
        open={sendTarget !== null}
        encId={sendTarget?.enc_id ?? null}
        isResend={sendTarget?.is_sent ?? false}
        customerName={sendTarget?.customer_name ?? null}
        customerEmail={null}
        customerPhone={sendTarget?.customer_phone ?? null}
        onClose={() => setSendTarget(null)}
        onSent={() => {
          void list.refetch();
        }}
      />

      {mismatchTarget?.name_mismatch ? (
        <NameMismatchModal
          open
          mismatch={mismatchTarget.name_mismatch}
          receiptRef={mismatchTarget.unique_id ?? mismatchTarget.reference ?? '—'}
          onClose={() => setMismatchTarget(null)}
        />
      ) : null}

      {notesTarget && (
        <NotesPanel
          open={notesTarget !== null}
          onClose={() => setNotesTarget(null)}
          entityType="receipt"
          encId={notesTarget.enc_id}
          entityLabel={`Receipt ${notesTarget.unique_id}`}
        />
      )}

      <ConfirmModal
        open={rejectTarget !== null}
        title="Reject receipt?"
        message={
          <div>
            <p style={{ marginTop: 0 }}>
              {rejectTarget !== null
                ? `Reject receipt ${rejectTarget.unique_id}? Provide a reason for the audit trail.`
                : ''}
            </p>
            <textarea
              aria-label="Rejection reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
              }}
              maxLength={2000}
            />
          </div>
        }
        confirmLabel="Reject"
        cancelLabel="Cancel"
        variant="danger"
        loading={rejecting}
        onCancel={() => {
          setRejectTarget(null);
          setRejectReason('');
        }}
        onConfirm={() => {
          if (rejectTarget !== null) void handleReject(rejectTarget, rejectReason);
        }}
      />
    </div>
  );
}
